import React, { useMemo, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  useTheme,
  Chip,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import StatsCard from './StatsCard';
import { useDailySales, useMonthToDateSales, useYearToDateSales } from '../hooks/useSalesData';
import { useProductMix, usePaymentAnalysis } from '../hooks/useAnalytics';

interface Store {
  id: string;
  name: string;
  address?: string;
  address1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  ownerName?: string;
  active?: number | boolean;
  brand?: string;
  currentEODID?: number;
  lastEODID?: number;
  totalSales?: number;
  orderCount?: number;
  averageOrderValue?: number;
}

interface StoreDetailViewProps {
  store: Store;
  onBack: () => void;
}

interface CategoryData {
  name: string;
  value: number;
  color: string;
}

const brandColors: Record<string, string> = {
  "Happy's Pizza": '#FF6B6B',
  "Savvy Sliders": '#4ECDC4',
  "BurgerFi": '#AF2326',
  "Fat Boys Pizza": '#FFD93D',
};

const StoreDetailView: React.FC<StoreDetailViewProps> = ({ store, onBack }) => {
  const theme = useTheme();
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const today = format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState<string>(yesterday); // Default to yesterday
  
  // Fetch real data for this specific store
  const { data: dailySales, isLoading: isDailyLoading } = useDailySales(selectedDate, store.id);
  const { data: mtdSales, isLoading: isMtdLoading } = useMonthToDateSales(undefined, store.id);
  const { data: ytdSales, isLoading: isYtdLoading } = useYearToDateSales(undefined, store.id);
  const { data: productMix, isLoading: isProductLoading } = useProductMix(selectedDate, store.id);
  const { data: paymentData, isLoading: isPaymentLoading } = usePaymentAnalysis(selectedDate, store.id);
  
  const isLoading = isDailyLoading || isMtdLoading || isYtdLoading || isProductLoading || isPaymentLoading;

  // Generate 30-day trend data (in production, this would come from an API)
  const salesTrendData = useMemo(() => {
    if (!dailySales) return [];
    
    // For demo purposes, generate trend based on current day's sales
    const baseSales = dailySales?.current?.totalSales || 0;
    
    return Array.from({ length: 30 }, (_, i) => {
      const date = format(subDays(new Date(), 29 - i), 'MM/dd');
      const variance = (Math.random() - 0.5) * 0.3;
      const sales = Math.round(baseSales * (1 + variance));
      const lastYearSales = Math.round(sales * 0.88 * (1 + variance * 0.7));
      
      return {
        date,
        sales,
        lastYearSales,
        orders: Math.round(sales / 32),
        avgOrder: 32
      };
    });
  }, [dailySales]);

  // Generate hourly data (in production, this would come from an API)
  const hourlyData = useMemo(() => {
    const peakHours = [11, 12, 17, 18, 19];
    const baseSales = (dailySales?.current?.totalSales || 0) / 24;
    
    return Array.from({ length: 24 }, (_, hour) => {
      const isPeak = peakHours.includes(hour);
      const hourlyBase = baseSales * (isPeak ? 2.5 : hour >= 6 && hour <= 22 ? 1.2 : 0.3);
      const variance = (Math.random() - 0.5) * 0.25;
      const sales = Math.round(hourlyBase * (1 + variance));
      
      return { 
        hour, 
        sales, 
        orders: Math.round(sales / 32),
        time: `${hour.toString().padStart(2, '0')}:00`
      };
    });
  }, [dailySales]);

  // Process product mix data
  const categoryData = useMemo<CategoryData[]>(() => {
    if (productMix?.categories) {
      return productMix.categories.map((cat: any, index: number) => ({
        name: cat.name,
        value: cat.percentage,
        color: ['#AF2326', '#C94144', '#E35E61', '#ED7B7E'][index % 4]
      }));
    }
    
    // Fallback data based on brand
    if (store.brand === "Happy's Pizza") {
      return [
        { name: 'Pizza', value: 55, color: '#FF6B6B' },
        { name: 'Wings', value: 20, color: '#FF8E8E' },
        { name: 'Subs', value: 15, color: '#FFB1B1' },
        { name: 'Beverages', value: 10, color: '#FFD4D4' }
      ];
    } else if (store.brand === "Savvy Sliders") {
      return [
        { name: 'Sliders', value: 45, color: '#4ECDC4' },
        { name: 'Sides', value: 25, color: '#7ED9D5' },
        { name: 'Beverages', value: 18, color: '#AEE5E2' },
        { name: 'Desserts', value: 12, color: '#DEF1EF' }
      ];
    } else {
      return [
        { name: 'Entrees', value: 50, color: '#AF2326' },
        { name: 'Sides', value: 22, color: '#C94144' },
        { name: 'Beverages', value: 16, color: '#E35E61' },
        { name: 'Desserts', value: 12, color: '#ED7B7E' }
      ];
    }
  }, [productMix, store.brand]);

  // Store metrics
  const storeMetrics = useMemo(() => {
    return {
      todaySales: dailySales?.current?.totalSales || 0,
      todayOrders: dailySales?.current?.orderCount || 0,
      avgOrderValue: dailySales?.current?.averageOrderValue || 0,
      mtdSales: mtdSales?.current?.totalSales || 0,
      ytdSales: ytdSales?.current?.totalSales || 0,
      growth: dailySales?.comparisons?.dayOverDay?.salesPercent || 0
    };
  }, [dailySales, mtdSales, ytdSales]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            ...glassStyle,
            p: 2,
            borderRadius: '12px',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontWeight: 600 }}
            >
              {entry.name}: ${entry.value.toLocaleString()}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              Back to Store Locations
            </Button>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {store.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Chip
                  icon={<StoreIcon />}
                  label={store.brand || 'Unknown'}
                  sx={{
                    backgroundColor: store.brand ? (brandColors[store.brand] || '#666') : '#666',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                {(store.address1 || store.city || store.state) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {[store.address1, store.city, [store.state, store.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                )}
                {store.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {store.phone}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Chip
              icon={storeMetrics.growth >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${Math.abs(storeMetrics.growth).toFixed(1)}% ${storeMetrics.growth >= 0 ? 'Growth' : 'Decline'}`}
              sx={{
                backgroundColor: storeMetrics.growth >= 0 ? 'success.main' : 'error.main',
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
        </Box>
      </motion.div>

      {/* Date Selector */}
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Daily Data for:
          </Typography>
          <ToggleButtonGroup
            value={selectedDate}
            exclusive
            onChange={(e, newDate) => newDate && setSelectedDate(newDate)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                border: `1px solid ${theme.palette.divider}`,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                }
              }
            }}
          >
            <ToggleButton value={yesterday}>Yesterday</ToggleButton>
            <ToggleButton value={today}>Today</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            sx={{ 
              width: 150,
              '& .MuiInputBase-input': {
                py: 0.75,
                fontSize: '0.8rem',
              }
            }}
          />
        </Box>
      </motion.div>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title={selectedDate === today ? "Today's Revenue" : selectedDate === yesterday ? "Yesterday's Revenue" : "Daily Revenue"}
            value={storeMetrics.todaySales}
            prefix="$"
            change={storeMetrics.growth}
            changeText="vs yesterday"
            icon={<TrendingUpIcon />}
            delay={0}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Orders"
            value={storeMetrics.todayOrders}
            change={storeMetrics.growth * 0.8}
            changeText="vs yesterday"
            icon={<TrendingUpIcon />}
            delay={0.1}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Average Order"
            value={storeMetrics.avgOrderValue}
            prefix="$"
            icon={<TrendingUpIcon />}
            delay={0.2}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="MTD Revenue"
            value={storeMetrics.mtdSales}
            prefix="$"
            change={mtdSales?.yearOverYear?.salesPercent}
            changeText="vs last month"
            icon={<TrendingUpIcon />}
            delay={0.3}
            color="error"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Sales Trend Chart */}
        <Grid item xs={12} lg={8}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 450 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Sales Trend (30 Days) - {store.name}
              </Typography>
              
              <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={salesTrendData}>
                  <defs>
                    <linearGradient id={`storeGradient`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={store.brand ? (brandColors[store.brand] || '#AF2326') : '#AF2326'} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={store.brand ? (brandColors[store.brand] || '#AF2326') : '#AF2326'} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="date" stroke="#A0AEC0" style={{ fontSize: '0.75rem' }} />
                  <YAxis stroke="#A0AEC0" style={{ fontSize: '0.75rem' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="lastYearSales"
                    stroke="#666"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    fillOpacity={0.1}
                    fill="#666"
                    name="Last Year Sales"
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke={store.brand ? (brandColors[store.brand] || '#AF2326') : '#AF2326'}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={`url(#storeGradient)`}
                    name="Current Sales"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>

        {/* Category Breakdown */}
        <Grid item xs={12} lg={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 450 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Product Mix
              </Typography>
              
              <ResponsiveContainer width="100%" height="60%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry: CategoryData, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <Box sx={{ mt: 2 }}>
                {categoryData.map((category: CategoryData) => (
                  <Box
                    key={category.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '3px',
                          backgroundColor: category.color,
                        }}
                      />
                      <Typography variant="body2">{category.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {category.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Hourly Performance */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.6 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Hourly Performance - {store.name}
              </Typography>

              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#A0AEC0"
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => `${value}:00`}
                  />
                  <YAxis stroke="#A0AEC0" style={{ fontSize: '0.75rem' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="sales"
                    fill={store.brand ? (brandColors[store.brand] || '#AF2326') : '#AF2326'}
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>

        {/* Store Performance Summary */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.7 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Performance Summary
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Year-to-Date Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      ${storeMetrics.ytdSales.toLocaleString()}
                    </Typography>
                    {ytdSales?.yearOverYear && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        {ytdSales.yearOverYear.salesPercent >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: ytdSales.yearOverYear.salesPercent >= 0 ? 
                              theme.palette.success.main : theme.palette.error.main 
                          }}
                        >
                          {Math.abs(ytdSales.yearOverYear.salesPercent).toFixed(1)}% from last year
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Month-to-Date Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      ${storeMetrics.mtdSales.toLocaleString()}
                    </Typography>
                    {mtdSales?.yearOverYear && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        {mtdSales.yearOverYear.salesPercent >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: mtdSales.yearOverYear.salesPercent >= 0 ? 
                              theme.palette.success.main : theme.palette.error.main 
                          }}
                        >
                          {Math.abs(mtdSales.yearOverYear.salesPercent).toFixed(1)}% from last year
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Store Owner
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {store.ownerName || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Store ID
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {store.id}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreDetailView;